// Cloud storage integrations for secure backup files
// Supports Google Drive, and can be extended for other providers

export interface CloudStorageProvider {
  name: string;
  id: string;
  icon: string;
  isAvailable: () => boolean;
  authenticate: () => Promise<boolean>;
  upload: (fileName: string, content: string, usePrivateStorage?: boolean) => Promise<string>; // Returns file URL or ID
  isAuthenticated: () => boolean;
  signOut: () => Promise<void>;
}

// Google Drive integration using Google Identity Services + Google API Client Library
export class GoogleDriveProvider implements CloudStorageProvider {
  name = 'Google Drive';
  id = 'google-drive';
  icon = '📁'; // Can be replaced with proper icon

  private accessToken: string | null = null;
  private readonly CLIENT_ID = __GOOGLE_DRIVE_CLIENT_ID__;
  private readonly DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest';
  private readonly SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.appdata';

  private gapiInitialized = false;
  private gisInitialized = false;

  constructor() {
    this.initializeAPIs();
  }

  private async initializeAPIs(): Promise<void> {
    // Load Google APIs
    if (!window.gapi) {
      await this.loadScript('https://apis.google.com/js/api.js');
    }

    if (!window.google?.accounts) {
      await this.loadScript('https://accounts.google.com/gsi/client');
    }

    // Initialize gapi
    if (!this.gapiInitialized) {
      await new Promise<void>(resolve => {
        window.gapi.load('client', resolve);
      });

      await window.gapi.client.init({
        discoveryDocs: [this.DISCOVERY_DOC],
      });

      this.gapiInitialized = true;
      console.log('[GoogleDrive] Google API client initialized');
    }

    // Initialize Google Identity Services
    if (!this.gisInitialized) {
      this.gisInitialized = true;
      console.log('[GoogleDrive] Google Identity Services initialized');
    }
  }

  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  isAvailable(): boolean {
    return !!this.CLIENT_ID && typeof window !== 'undefined';
  }

  async authenticate(): Promise<boolean> {
    if (!this.isAvailable()) {
      throw new Error(
        'Google Drive integration is not available in this environment'
      );
    }

    await this.initializeAPIs();

    return new Promise((resolve, reject) => {
      try {
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: this.CLIENT_ID,
          scope: this.SCOPES,
          callback: (response: any) => {
            if (response.error) {
              console.error('[GoogleDrive] Authentication error:', response.error);
              reject(new Error(`Google Drive authentication failed: ${response.error}`));
              return;
            }

            this.accessToken = response.access_token;
            window.gapi.client.setToken({ access_token: this.accessToken });
            console.log('[GoogleDrive] Successfully authenticated');
            resolve(true);
          },
        });

        tokenClient.requestAccessToken({ prompt: 'consent' });
      } catch (error) {
        console.error('[GoogleDrive] Authentication initialization failed:', error);
        reject(new Error(`Google Drive authentication setup failed: ${error}`));
      }
    });
  }

  isAuthenticated(): boolean {
    return !!this.accessToken;
  }

  async signOut(): Promise<void> {
    if (this.accessToken) {
      window.google?.accounts.oauth2.revoke(this.accessToken);
      this.accessToken = null;
      window.gapi?.client.setToken(null);
      console.log('[GoogleDrive] Signed out successfully');
    }
  }

  async upload(fileName: string, content: string, usePrivateStorage: boolean = true): Promise<string> {
    if (!this.isAuthenticated()) {
      throw new Error('Not authenticated with Google Drive');
    }

    await this.initializeAPIs();

    if (usePrivateStorage) {
      // Try appDataFolder first (private, hidden)
      try {
        const fileId = await this.uploadToAppDataFolder(fileName, content);
        console.log('[GoogleDrive] File uploaded to appDataFolder successfully:', fileId);
        return fileId;
      } catch (error) {
        console.warn('[GoogleDrive] AppDataFolder upload failed, trying fallback to regular folder:', error);
        // Fallback to visible folder if appDataFolder fails
        const fileId = await this.uploadToAppFolder(fileName, content);
        console.log('[GoogleDrive] File uploaded to app folder successfully:', fileId);
        return fileId;
      }
    } else {
      // Upload to "Backups" folder (visible in Drive)
      try {
        const fileId = await this.uploadToAppFolder(fileName, content);
        console.log('[GoogleDrive] File uploaded to app folder successfully:', fileId);
        return fileId;
      } catch (error) {
        console.error('[GoogleDrive] Upload failed:', error);
        throw new Error(
          `Failed to upload to Google Drive: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }
  }

  private async uploadToAppDataFolder(fileName: string, content: string): Promise<string> {
    const metadata = {
      name: fileName,
      parents: ['appDataFolder'], // Store in app-specific folder for privacy
    };

    return this.performUpload(metadata, content);
  }

  private async uploadToAppFolder(fileName: string, content: string): Promise<string> {
    // First, find or create "EmbarkAI Wallet Backups" folder
    const folderName = 'EmbarkAI Wallet Backups';
    const folderId = await this.findOrCreateFolder(folderName);

    const metadata = {
      name: fileName,
      parents: [folderId],
    };

    return this.performUpload(metadata, content);
  }

  private async findOrCreateFolder(folderName: string): Promise<string> {
    // Search for existing folder
    const searchResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`,
      {
        headers: { Authorization: `Bearer ${this.accessToken}` },
        credentials: 'include'
      }
    );

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for folder: ${searchResponse.status}`);
    }

    const searchResult = await searchResponse.json();

    if (searchResult.files && searchResult.files.length > 0) {
      return searchResult.files[0].id; // Return existing folder ID
    }

    // Create new folder
    const createResponse = await fetch('https://www.googleapis.com/drive/v3/files', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        name: folderName,
        mimeType: 'application/vnd.google-apps.folder',
      }),
    });

    if (!createResponse.ok) {
      throw new Error(`Failed to create folder: ${createResponse.status}`);
    }

    const createResult = await createResponse.json();
    console.log(`[GoogleDrive] Created folder '${folderName}':`, createResult.id);
    return createResult.id;
  }

  private async performUpload(metadata: any, content: string): Promise<string> {
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', new Blob([content], { type: 'application/json' }));

    const response = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
        },
        credentials: 'include',
        body: form,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Google Drive upload failed: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const result = await response.json();
    return result.id; // Return file ID
  }
}

// Factory function to get available cloud storage providers
export function getAvailableCloudProviders(): CloudStorageProvider[] {
  const providers: CloudStorageProvider[] = [];

  // Google Drive
  const googleDrive = new GoogleDriveProvider();
  if (googleDrive.isAvailable()) {
    providers.push(googleDrive);
  }

  // Future: Add more providers (Dropbox, OneDrive, etc.)

  return providers;
}

// Type definitions for global APIs
declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}
